<template>
  <div class="admin-page">
    <div class="admin-toolbar admin-toolbar--end">
      <button class="btn-primary" @click="showCreateModal = true">创建新课程</button>
    </div>

    <!-- Course List -->
    <div class="course-list">
      <div class="search-bar">
        <input 
          type="text" 
          v-model="searchKeyword" 
          placeholder="查找课程..."
          @input="handleSearch"
        >
        <select v-model.number="auditFilter" @change="handleSearch">
          <option :value="0">审核：全部</option>
          <option :value="1">待审核</option>
          <option :value="2">已通过</option>
          <option :value="3">已驳回</option>
        </select>
        <select v-model.number="removedFilter" @change="handleSearch">
          <option :value="0">删除：全部</option>
          <option :value="1">未删除</option>
          <option :value="2">已删除</option>
        </select>
        <select v-model.number="stateFilter" @change="handleSearch">
          <option :value="0">上架：全部</option>
          <option :value="1">上架</option>
          <option :value="2">已下架</option>
        </select>
      </div>
      <p class="audit-hint">C 端只展示「未删除 + 上架 + 审核通过」的课程，与测评一致。用户新建默认待审核。</p>
      
      <table class="course-table">
        <thead>
          <tr>
            <th>名称</th>
            <th>分类</th>
            <th>标签</th>
            <th>等级</th>
            <th>封面</th>
            <th>价格</th>
            <th>审核</th>
            <th>上架</th>
            <th>佣金档位</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="course in courses" :key="course.kcId">
            <td>{{ course.name }}</td>
            <td>{{ course.category }}</td>
            <td class="tag-cell">
              <ContentTagSelect
                :model-value="(course.tagIds || []).map(id => Number(id)).filter(id => id > 0)"
                scope="course"
                placeholder="加减标签"
                @update:model-value="(ids) => saveRowTags(course, ids)"
              />
            </td>
            <td>{{ getLevelText(course.level) }}</td>
            <td>
              <img
                v-if="course.banner"
                :src="course.banner"
                style="max-width: 80px; max-height: 40px; border-radius: 2px;"
                @error="$event.target.style.display='none'"
              />
              <span v-if="!course.banner" class="banner-missing">无</span>
            </td>
            <td>{{ formatPrice(course.fee) }}</td>
            <td>{{ auditText(course.auditStatus) }}</td>
            <td>{{ Number(course.state) === 1 ? '上架' : '已下架' }}</td>
            <td>
              <select
                v-if="canPresetCourseCommission(course)"
                class="commission-select"
                :value="course.platformCommissionPct || 0"
                @change="onCommissionChange('course', course.kcId, Number($event.target.value), course)"
              >
                <option
                  v-for="opt in COMMISSION_TIER_OPTIONS"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
              <span v-else>—</span>
            </td>
            <td>
              <button class="btn-view" @click="viewCourse(course)">查看</button>
              <button class="btn-edit" @click="editCourse(course)">编辑</button>
              <button class="btn-delete" @click="deleteCourse(course)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>

      <AdminPagination
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="[20, 50, 100]"
        :total="total"
        @update:current-page="changePage"
        @size-change="handlePageSizeChange"
      />
    </div>

    <!-- Create/Edit Modal -->
    <div class="modal" v-if="showCreateModal || showEditModal">
      <div class="modal-content">
        <h3>{{ showEditModal ? '编辑课程' : '创建课程' }}</h3>
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label>名称</label>
            <input type="text" v-model="formData.name" required>
          </div>
          
          <div class="form-group">
            <label>分类</label>
            <input type="text" v-model="formData.category" required>
          </div>
          
          <div class="form-group">
            <label>等级</label>
            <select v-model="formData.level" @change="onLevelChange" required>
              <option :value="1">基础</option>
              <option :value="2">进阶</option>
              <option :value="3">高阶</option>
            </select>
          </div>

          <div class="form-group">
            <label>定价（元）</label>
            <input type="number" v-model.number="formData.feeYuan" min="0" step="0.01" placeholder="0 表示免费">
          </div>
          
          <div class="form-group">
            <label>描述</label>
            <textarea v-model="formData.description" required></textarea>
          </div>

          <div class="form-group">
            <label>内容标签</label>
            <ContentTagSelect v-model="formData.tagIds" scope="course" />
          </div>
          
          <div class="form-group">
            <label>知识点大纲</label>
            <div v-if="!formData.kcId" class="tree-hint">请先保存课程基本信息，保存后可在此课程下新建知识点并编排大纲。</div>
            <div class="tree-editor">
              <div class="tree-toolbar">
                <button type="button" @click="addNode" :disabled="!formData.kcId">添加节点</button>
                <button type="button" @click="removeNode" :disabled="!formData.kcId">移除节点</button>
                <button type="button" class="btn-kp-create" @click="openKpCreateModal" :disabled="!formData.kcId">新建知识点</button>
              </div>
              <div class="tree-view">
                <div v-for="(node, index) in treeData.children" :key="index" class="tree-node">
                  <div class="node-content">
                    <input v-model="node.seqname" placeholder="序号" class="node-input">
                    <select v-model="node.kpID" class="node-select" @change="onKpIdChange(node)" :disabled="!formData.kcId">
                      <option value="">选择知识点</option>
                      <option v-for="kp in courseKnowledgePoints" :key="kp.kpId" :value="kp.kpId">
                        {{ kp.name }} ({{ kp.category }})
                      </option>
                    </select>
                    <input v-model="node.name" placeholder="名称" class="node-input" readonly>
                    <button type="button" @click="addChildNode(node)" :disabled="!formData.kcId">添加子节点</button>
                    <button type="button" @click="removeChildNode(node)" :disabled="!formData.kcId">移除</button>
                  </div>
                  <div class="node-children" v-if="node.children && node.children.length">
                    <div v-for="(child, childIndex) in node.children" :key="childIndex" class="tree-node">
                      <div class="node-content">
                        <input v-model="child.seqname" placeholder="序号" class="node-input">
                        <select v-model="child.kpID" class="node-select" @change="onKpIdChange(child)" :disabled="!formData.kcId">
                          <option value="">选择知识点</option>
                          <option v-for="kp in courseKnowledgePoints" :key="kp.kpId" :value="kp.kpId">
                            {{ kp.name }} ({{ kp.category }})
                          </option>
                        </select>
                        <input v-model="child.name" placeholder="名称" class="node-input" readonly>
                        <button type="button" @click="addChildNode(child)" :disabled="!formData.kcId">添加子节点</button>
                        <button type="button" @click="removeChildNode(child)" :disabled="!formData.kcId">移除</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group" v-if="showEditModal">
            <label>审核状态</label>
            <select v-model.number="formData.auditStatus">
              <option :value="0">待审核</option>
              <option :value="1">通过</option>
              <option :value="2">驳回</option>
            </select>
          </div>
          <div class="form-group" v-if="showEditModal">
            <label>审核说明</label>
            <input type="text" v-model="formData.auditMessage" placeholder="驳回时填写原因">
          </div>
          <div class="form-group" v-if="showEditModal">
            <label>上架</label>
            <select v-model.number="formData.state">
              <option :value="1">上架</option>
              <option :value="0">下架</option>
            </select>
          </div>
          <div class="form-group" v-if="showEditModal">
            <label>删除</label>
            <select v-model.number="formData.removed">
              <option :value="0">未删除</option>
              <option :value="1">已删除</option>
            </select>
          </div>

          <div class="form-group">
            <label>封面图</label>
            <div class="banner-upload-wrap">
              <div v-if="bannerBroken" class="banner-broken-tip">图片无法加载，请重新上传</div>
              <el-upload
                class="banner-uploader"
                action="/api/v1/upload"
                :show-file-list="false"
                :on-success="handleBannerSuccess"
                :before-upload="beforeBannerUpload"
                :headers="uploadHeaders"
                :data="uploadData"
                accept="image/png,image/jpeg,image/webp"
              >
                <img
                  v-if="formData.banner && !bannerBroken"
                  :src="formData.banner"
                  class="banner"
                  @error="bannerBroken = true"
                />
                <el-icon v-else class="banner-uploader-icon"><Plus /></el-icon>
              </el-upload>
              <button
                v-if="formData.banner"
                type="button"
                class="btn-clear-banner"
                @click="clearBanner"
              >清除封面</button>
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn-primary">
              {{ showEditModal ? '更新' : '创建' }}
            </button>
            <button type="button" class="btn-secondary" @click="closeModal">取消</button>
          </div>
        </form>
      </div>
    </div>

    <!-- 课程内新建知识点 -->
    <div class="modal" v-if="showKpCreateModal">
      <div class="modal-content modal-content--narrow">
        <h3>新建知识点（{{ formData.name || '当前课程' }}）</h3>
        <form @submit.prevent="createCourseKnowledgePoint">
          <div class="form-group">
            <label>名称 *</label>
            <input v-model="kpFormData.name" type="text" required>
          </div>
          <div class="form-group">
            <label>分类 *</label>
            <input v-model="kpFormData.category" type="text" :placeholder="formData.category || '与课程分类一致'" required>
          </div>
          <div class="form-group">
            <label>等级 *</label>
            <select v-model="kpFormData.level" required>
              <option :value="1">基础</option>
              <option :value="2">进阶</option>
              <option :value="3">高阶</option>
            </select>
          </div>
          <div class="form-group">
            <label>描述</label>
            <textarea v-model="kpFormData.description" rows="3"></textarea>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn-primary">创建并加入本课程</button>
            <button type="button" class="btn-secondary" @click="showKpCreateModal = false">取消</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
// import axios from 'axios'
import { Plus } from '@element-plus/icons-vue'
import { validatePhotoUpload, adminUploadFormData, uploadResponseToUrl } from '@/utils/photoUpload'
import AdminPagination from '@/components/AdminPagination.vue'
import ContentTagSelect from '@/components/ContentTagSelect.vue'
import { bindContentTags } from '@/utils/contentTags'
import {
  COMMISSION_TIER_OPTIONS,
  canPresetCourseCommission,
  setContentCommissionTier
} from '@/utils/commissionTier'

export default {
  name: 'KnowledgeCourseAdmin',
  components: { AdminPagination, Plus, ContentTagSelect },
  data() {
    return {
      COMMISSION_TIER_OPTIONS,
      courses: [],
      knowledgePoints: [],
      currentPage: 1,
      pageSize: 50,
      total: 0,
      totalPages: 1,
      searchKeyword: '',
      auditFilter: 0,
      removedFilter: 0,
      stateFilter: 0,
      showCreateModal: false,
      showEditModal: false,
      bannerBroken: false,
      formData: {
        name: '',
        category: '',
        level: 1,
        feeYuan: 0,
        description: '',
        kpTree: '',
        banner: '',
        tagIds: [],
        auditStatus: 1,
        auditMessage: '',
        state: 1,
        removed: 0
      },
      treeData: {
        children: []
      },
      showKpCreateModal: false,
      kpFormData: {
        name: '',
        category: '',
        level: 1,
        description: ''
      }
    }
  },
  computed: {
    courseKnowledgePoints() {
      if (!this.formData.kcId) return []
      return this.knowledgePoints.filter(kp => kp.kcId === this.formData.kcId)
    },
    uploadHeaders() {
      if (typeof localStorage !== 'undefined') {
        return { Authorization: 'Bearer ' + (localStorage.getItem('jwt_token') || '') }
      }
      return {}
    },
    uploadData() {
      return adminUploadFormData('courses')
    }
  },
  created() {
    this.fetchCourses()
    this.fetchKnowledgePoints()
  },
  methods: {
    canPresetCourseCommission,
    async onCommissionChange(contentType, contentId, pct, row) {
      const prev = row.platformCommissionPct || 0
      try {
        const data = await setContentCommissionTier(contentType, contentId, pct)
        if (data.code === 200) {
          row.platformCommissionPct = pct
          this.$message.success('佣金档位已更新')
        } else {
          row.platformCommissionPct = prev
          this.$message.error(data.message || '更新失败')
        }
      } catch (error) {
        row.platformCommissionPct = prev
        this.$message.error('更新佣金档位失败')
      }
    },
    async fetchCourses() {
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/courses/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            page: this.currentPage,
            pageSize: this.pageSize,
            keyword: this.searchKeyword,
            adminView: 1,
            auditFilter: this.auditFilter,
            removedFilter: this.removedFilter,
            stateFilter: this.stateFilter
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.courses = data.data.list
          this.total = data.data.total
          this.totalPages = Math.ceil(data.data.total / this.pageSize) || 1
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
        } else {
          this.$message.error(data.message || '获取课程列表失败')
        }
      } catch (error) {
        console.error('Failed to fetch courses:', error)
        this.$message.error('获取课程列表失败')
      }
    },
    async fetchKnowledgePoints(kcId = 0) {
      try {
        const token = localStorage.getItem('jwt_token')
        const body = {
          page: 1,
          pageSize: 1000,
          keyword: ''
        }
        if (kcId) body.kcId = kcId
        const response = await fetch('/api/v1/knowledge/points/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        })
        const data = await response.json()
        if (data.code === 200) {
          if (kcId) {
            const others = this.knowledgePoints.filter(kp => kp.kcId !== kcId)
            this.knowledgePoints = [...others, ...data.data.list]
          } else {
            this.knowledgePoints = data.data.list
          }
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
        } else {
          this.$message.error(data.message || '获取知识点列表失败')
        }
      } catch (error) {
        console.error('Failed to fetch knowledge points:', error)
        this.$message.error('获取知识点列表失败')
      }
    },
    openKpCreateModal() {
      if (!this.formData.kcId) {
        this.$message.warning('请先保存课程')
        return
      }
      this.kpFormData = {
        name: '',
        category: this.formData.category || '',
        level: parseInt(this.formData.level, 10) || 1,
        description: ''
      }
      this.showKpCreateModal = true
    },
    async createCourseKnowledgePoint() {
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/points/create', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            kcId: this.formData.kcId,
            name: this.kpFormData.name,
            category: this.kpFormData.category,
            level: parseInt(this.kpFormData.level, 10),
            description: this.kpFormData.description
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          await this.fetchKnowledgePoints(this.formData.kcId)
          this.showKpCreateModal = false
          this.$message.success('知识点已创建，可在下方大纲中选择绑定')
        } else {
          this.$message.error(data.message || '创建知识点失败')
        }
      } catch (error) {
        console.error('Failed to create knowledge point:', error)
        this.$message.error('创建知识点失败')
      }
    },
    onKpIdChange(node) {
      if (node.kpID) {
        const selectedKp = this.knowledgePoints.find(kp => kp.kpId === node.kpID)
        if (selectedKp) {
          node.name = selectedKp.name
        }
      } else {
        node.name = ''
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchCourses()
    },
    changePage(page) {
      this.currentPage = page
      this.fetchCourses()
    },
    handlePageSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.fetchCourses()
    },
    async saveRowTags(course, ids) {
      const prev = (course.tagIds || []).map((id) => Number(id)).filter((id) => id > 0)
      course.tagIds = ids || []
      try {
        const data = await bindContentTags('course', course.kcId, ids)
        if (data.code === 200) {
          this.$message.success('标签已更新')
        } else {
          course.tagIds = prev
          this.$message.error(data.message || '更新标签失败')
        }
      } catch (error) {
        course.tagIds = prev
        this.$message.error('更新标签失败')
      }
    },
    editCourse(course) {
      this.formData = { 
        ...course,
        level: parseInt(course.level),
        feeYuan: (course.fee || 0) / 100,
        banner: course.banner || '',
        tagIds: (course.tagIds || []).map((id) => Number(id)).filter((id) => id > 0),
        auditStatus: Number(course.auditStatus) || 0,
        auditMessage: course.auditMessage || '',
        state: Number(course.state) === 0 ? 0 : 1,
        removed: Number(course.removed) === 1 ? 1 : 0
      }
      this.bannerBroken = false
      this.treeData = JSON.parse(course.kpTree || '{"children":[]}')
      this.fetchKnowledgePoints(course.kcId)
      this.showEditModal = true
    },
    viewCourse(course) {
      this.$router.push(`/knowledge/courses/display/${course.kcId}`)
    },
    async deleteCourse(course) {
      if (confirm('确定要删除这门课程吗？')) {
        try {
          const token = localStorage.getItem('jwt_token')
          const response = await fetch('/api/v1/knowledge/courses/delete', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ kcId: course.kcId })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.fetchCourses()
            this.$message.success('课程删除成功')
          } else if (data.code === 401) {
            this.$message.error('登录已过期，请重新登录')
          } else if (data.code === 403) {
            this.$message.error('权限不足，需要管理员权限才能执行此操作')
          } else {
            this.$message.error(data.message || '删除课程失败')
          }
        } catch (error) {
          console.error('Failed to delete course:', error)
          if (error.status === 403) {
            this.$message.error('权限不足，需要管理员权限才能执行此操作')
          } else {
            this.$message.error('删除课程失败')
          }
        }
      }
    },
    async handleSubmit() {
      try {
        const token = localStorage.getItem('jwt_token')
        const payload = {
          ...this.formData,
          level: parseInt(this.formData.level),
          kpTree: JSON.stringify(this.treeData),
          banner: this.formData.banner || '',
          fee: Math.round((Number(this.formData.feeYuan) || 0) * 100),
          tagIds: (this.formData.tagIds || []).map((id) => Number(id)).filter((id) => id > 0),
          auditStatus: Number(this.formData.auditStatus) || 0,
          auditMessage: this.formData.auditMessage || '',
          state: Number(this.formData.state) === 0 ? 0 : 1,
          removed: Number(this.formData.removed) === 1 ? 1 : 0
        }
        delete payload.feeYuan
        
        const url = this.showEditModal 
          ? '/api/v1/knowledge/courses/update'
          : '/api/v1/knowledge/courses/create'
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })
        
        const data = await response.json()
        if (data.code === 200) {
          if (this.showEditModal) {
            this.closeModal()
            this.fetchCourses()
            this.$message.success('课程更新成功')
          } else {
            const created = data.data || {}
            this.formData.kcId = created.kcId
            this.showCreateModal = false
            this.showEditModal = true
            await this.fetchKnowledgePoints(created.kcId)
            this.fetchCourses()
            this.$message.success('课程已创建，可继续新建知识点并编排大纲')
          }
        } else if (data.code === 401) {
          this.$message.error('登录已过期，请重新登录')
        } else if (data.code === 403) {
          this.$message.error('权限不足，需要管理员权限才能执行此操作')
        } else {
          this.$message.error(data.message || (this.showEditModal ? '更新课程失败' : '创建课程失败'))
        }
      } catch (error) {
        console.error('Failed to save course:', error)
        if (error.status === 403) {
          this.$message.error('权限不足，需要管理员权限才能执行此操作')
        } else {
          this.$message.error(this.showEditModal ? '更新课程失败' : '创建课程失败')
        }
      }
    },
    clearBanner() {
      this.formData.banner = ''
      this.bannerBroken = false
    },
    closeModal() {
      this.showCreateModal = false
      this.showEditModal = false
      this.bannerBroken = false
      this.formData = {
        name: '',
        category: '',
        level: 1,
        feeYuan: 0,
        description: '',
        kpTree: '',
        banner: '',
        tagIds: [],
        auditStatus: 1,
        auditMessage: '',
        state: 1,
        removed: 0
      }
      this.treeData = { children: [] }
    },
    addNode() {
      this.treeData.children.push({
        kpID: '',
        seqname: '',
        name: '',
        children: []
      })
    },
    removeNode() {
      if (this.treeData.children.length > 0) {
        this.treeData.children.pop()
      }
    },
    addChildNode(node) {
      if (!node.children) {
        this.$set(node, 'children', [])
      }
      node.children.push({
        kpID: '',
        seqname: '',
        name: '',
        children: []
      })
    },
    removeChildNode(node) {
      const index = this.treeData.children.indexOf(node)
      if (index > -1) {
        this.treeData.children.splice(index, 1)
      }
    },
    getLevelText(level) {
      return { 1: '基础', 2: '进阶', 3: '高阶' }[level] || '未知'
    },
    auditText(status) {
      return { 0: '待审核', 1: '已通过', 2: '已驳回' }[Number(status)] || '未知'
    },
    onLevelChange() {
      // 确保 level 是数字类型
      this.formData.level = parseInt(this.formData.level)
    },
    handleBannerSuccess(response) {
      if (response.code === 200) {
        this.formData.banner = uploadResponseToUrl(response)
        this.bannerBroken = false
        this.$message.success('图片上传成功')
      } else {
        this.$message.error(response.message || '图片上传失败')
      }
    },
    beforeBannerUpload(file) {
      return validatePhotoUpload(file, (msg) => this.$message.error(msg))
    },
    formatPrice(fee) {
      if (!fee || fee === 0) return '免费'
      const yuan = fee / 100
      return `¥${yuan.toFixed(2)}`
    }
  }
}
</script>

<style scoped>
.admin-page {
  padding: 20px;
  max-width: 1800px;
  margin: 0 auto;
}

.tree-hint {
  margin-bottom: 8px;
  font-size: 13px;
  color: #909399;
}

.btn-kp-create {
  background: #67c23a;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
}

.modal-content--narrow {
  max-width: 480px;
}

.search-bar {
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.search-bar input,
.search-bar select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.search-bar input {
  min-width: 200px;
  flex: 1;
}

.audit-hint {
  margin: 0 0 16px;
  font-size: 13px;
  color: #909399;
}

.course-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.course-table th,
.course-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.tag-cell {
  min-width: 220px;
  vertical-align: middle;
}

.commission-select {
  max-width: 180px;
  font-size: 12px;
}

.course-table th {
  background-color: #f5f5f5;
}

.btn-primary,
.btn-secondary,
.btn-view,
.btn-edit,
.btn-delete {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 8px;
}

.btn-primary {
  background-color: #4CAF50;
  color: white;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #333;
}

.btn-view {
  background-color: #FF9800;
  color: white;
}

.btn-edit {
  background-color: #2196F3;
  color: white;
}

.btn-delete {
  background-color: #f44336;
  color: white;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 4px;
  width: 80%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.form-group textarea {
  height: 100px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.tree-editor {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
}

.tree-toolbar {
  margin-bottom: 15px;
}

.tree-node {
  margin: 5px 0;
}

.node-content {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 5px;
}

.node-input {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  flex: 1;
}

.node-select {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  flex: 2;
}

.node-children {
  margin-left: 20px;
  padding-left: 10px;
  border-left: 2px solid #eee;
}

.banner-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 200px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}
.banner-uploader-icon {
  font-size: 32px;
  color: #999;
}
.banner {
  max-width: 200px;
  max-height: 120px;
  border-radius: 4px;
  display: block;
  object-fit: cover;
}
.banner-upload-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}
.banner-broken-tip {
  color: #e6a23c;
  font-size: 13px;
}
.btn-clear-banner {
  padding: 4px 12px;
  font-size: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}
.banner-missing {
  color: #999;
  font-size: 12px;
}
</style> 